import Fabric from "../types/Fabric";

/**
 * Resolves which fabrics should be treated as "default" for a given product.
 *
 * Priority:
 *   1. If the product (adjustment) has an explicit `defaultFabrics` list in
 *      Contentful — use ONLY those fabrics (per-product override).
 *      Example: Dillon Black is the default for healthcare chairs, but not
 *      for any other chair that uses the same fabric.
 *   2. Otherwise — fall back to the legacy global `fabric.default` flag.
 *
 * This keeps existing products working with no changes in Contentful: the
 * override is purely opt-in.
 */
export const getDefaultFabrics = (
  fabrics: Fabric[] | undefined,
  adjustment: { defaultFabrics?: Fabric[] } | null | undefined
): Fabric[] => {
  // Per-product override — return it directly, regardless of whether the
  // fabric is also present in the product's `fabrics` array. This lets editors
  // add a default fabric on a single chair without having to also add it to
  // the chair's full fabric list.
  if (adjustment?.defaultFabrics?.length) {
    return adjustment.defaultFabrics;
  }

  if (!fabrics) return [];
  return fabrics.filter((fabric) => fabric.default);
};

/**
 * Inverse of `getDefaultFabrics` — fabrics that are NOT defaults for this
 * product. Used by the "Express" / standard colors carousel that intentionally
 * hides the default fabrics.
 */
export const getNonDefaultFabrics = (
  fabrics: Fabric[] | undefined,
  adjustment: { defaultFabrics?: Fabric[] } | null | undefined
): Fabric[] => {
  if (!fabrics) return [];

  // If a per-product override is set, "non-default" means everything in the
  // product's fabric list that isn't part of the override.
  if (adjustment?.defaultFabrics?.length) {
    const overrideIds = new Set(adjustment.defaultFabrics.map((f) => f.id));
    return fabrics.filter((fabric) => !overrideIds.has(fabric.id));
  }

  return fabrics.filter((fabric) => !fabric.default);
};

/**
 * Predicate version — true if the given fabric is a default for this product.
 * Convenient inside `.map(...)` chains where we can't pre-filter (e.g. the
 * options.controller renders both default and non-default in the same loop).
 */
export const isDefaultFabric = (
  fabric: Fabric,
  adjustment: { defaultFabrics?: Fabric[] } | null | undefined
): boolean => {
  if (adjustment?.defaultFabrics?.length) {
    return adjustment.defaultFabrics.some((f) => f.id === fabric.id);
  }
  return !!fabric.default;
};
